import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Select, RadioGroup, Label } from '@wit-support/ui';
import { FieldShell } from './field-shell.js';
import { readMeta } from '../lib/read-meta.js';
import { getDiscriminatedBranches, getDiscriminator, getLiteralValue, getObjectShape, isObject, } from '../lib/walk-schema.js';
import { RenderSchema } from '../render-schema.js';
export function DiscriminatedWidget(props) {
    const { schema, meta, label, description, hint, fieldState, name } = props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctx = useFormContext();
    const disc = getDiscriminator(schema);
    const branches = getDiscriminatedBranches(schema);
    const prefix = name ?? '';
    const kindPath = prefix ? `${prefix}.${disc}` : (disc ?? 'kind');
    const branchByKind = React.useMemo(() => {
        const map = {};
        for (const branch of branches) {
            if (!isObject(branch))
                continue;
            const shape = getObjectShape(branch);
            const discField = disc ? shape[disc] : undefined;
            const value = discField ? String(getLiteralValue(discField) ?? '') : '';
            if (!value)
                continue;
            const bMeta = readMeta(branch);
            map[value] = { branch, label: bMeta.label ?? value };
        }
        return map;
    }, [branches, disc]);
    const kinds = Object.keys(branchByKind);
    const activeKind = String(ctx.watch(kindPath) ?? kinds[0] ?? '');
    const cache = React.useRef({});
    const swapKind = (next) => {
        const current = activeKind;
        if (current && current !== next) {
            // Cache current branch's values (the whole subtree under `prefix` except the kind path).
            const snapshot = prefix ? ctx.getValues(prefix) : ctx.getValues();
            cache.current[current] = snapshot;
        }
        // Restore cached values for the next kind (if any), preserving kind field.
        const cached = cache.current[next];
        if (cached && typeof cached === 'object') {
            const restored = { ...cached, [disc ?? 'kind']: next };
            if (prefix)
                ctx.setValue(prefix, restored, { shouldValidate: false, shouldDirty: true });
            else
                ctx.reset(restored, { keepDefaultValues: true });
        }
        else {
            ctx.setValue(kindPath, next, { shouldValidate: false, shouldDirty: true });
        }
    };
    const options = kinds.map((k) => ({ value: k, label: branchByKind[k].label }));
    const active = branchByKind[activeKind]?.branch;
    const Picker = options.length <= 4 ? (_jsx(RadioGroup, { value: activeKind, onValueChange: swapKind, name: kindPath, children: options.map((opt) => {
            const id = `${kindPath}__${opt.value}`;
            return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RadioGroup.Item, { value: opt.value, id: id }), _jsx(Label, { htmlFor: id, children: opt.label })] }, opt.value));
        }) })) : (_jsxs(Select, { value: activeKind, onValueChange: swapKind, name: kindPath, children: [_jsx(Select.Trigger, { id: kindPath, children: _jsx(Select.Value, { placeholder: "Select\\u2026" }) }), _jsx(Select.Content, { children: options.map((opt) => (_jsx(Select.Item, { value: opt.value, children: opt.label }, opt.value))) })] }));
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(FieldShell, { meta: meta, fieldState: fieldState, label: label, description: description, hint: hint, htmlFor: kindPath, children: Picker }), active ? _jsx(RenderSchema, { schema: active, prefix: prefix, skip: disc ? [disc] : [] }) : null] }));
}
//# sourceMappingURL=discriminated.js.map